import type { Signal } from '@angular/core';
import { Service, computed, inject } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import type { FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import type {
  OnyxConfirmModalData,
  OnyxDropdownOptionsSource,
  OnyxLabelLink,
  OnyxOptionsGroup,
} from '@onyx/angular';
import {
  ActionHelper,
  OnyxConfirmModalComponent,
  OnyxModalService,
  OnyxToastService,
} from '@onyx/angular';
import { map, startWith, switchMap } from 'rxjs';
import { AuthStore } from '../../../../../auth/common/stores/auth.store';
import { CommonHelper } from '../../../../../common/helpers/common.helper';
import { NavigationHelper } from '../../../../../common/helpers/navigation.helper';
import type { HelperOptions } from '../../../../../common/interfaces/utilities/helper-options';
import type { BaseModalData } from '../components/base-modal/base-modal.component';
import { BaseModalComponent } from '../components/base-modal/base-modal.component';
import { BasesRoute } from '../enums/bases-route';
import type { Base, SimplifiedBase } from '../interfaces/base';
import { BasesService } from '../services/bases.service';

export interface BaseDropdownActions {
  link: Signal<OnyxLabelLink | null>;
  hasAdd: Signal<boolean>;
  add: () => void;
}

export const BASE_HEADER_ACTIONS = ['edit'] as const;

@Service()
export class BaseHelper {
  private readonly authStore = inject(AuthStore);
  private readonly router = inject(Router);
  private readonly modalService = inject(OnyxModalService);
  private readonly basesService = inject(BasesService);
  private readonly translateService = inject(TranslateService);
  private readonly toastService = inject(OnyxToastService);

  private readonly I18N = 'bases';

  public getBasesSource(): OnyxDropdownOptionsSource<Base> {
    return {
      list: (query) => this.basesService.searchBases(query),
      get: (uuid) =>
        this.basesService.getBase(uuid).pipe(
          map((base) => ({
            name: base.name,
            value: base,
          })),
        ),
      idKey: 'uuid',
    };
  }

  public getOptions(
    base: Base,
    options: HelperOptions,
    exclude: readonly (typeof BASE_HEADER_ACTIONS)[number][] = [],
  ): OnyxOptionsGroup<() => void>[] {
    return [
      {
        id: '0.default',
        options:
          !exclude.includes('edit') &&
          this.authStore.hasPermission('bases.edit')
            ? [
                {
                  name: 'labels.edit',
                  value: () => this.edit(base.uuid, options),
                  leftIcon: { name: 'edit-outlined' as const, size: 20 },
                  leftIconColor: 'gray-alt' as const,
                },
              ]
            : [],
      },
      {
        id: '1.delete',
        options: this.authStore.hasPermission('bases.delete')
          ? [
              {
                name: 'labels.delete',
                value: () => this.delete(base, options),
                leftIcon: { name: 'delete-outlined' as const, size: 20 },
                leftIconColor: 'red' as const,
              },
            ]
          : [],
      },
    ].filter((group) => group.options.length);
  }

  public getPreviewLink(uuid: string | null): OnyxLabelLink | null {
    return CommonHelper.getPreviewLink(uuid, (uuid) => this.openModal(uuid));
  }

  public getDropdownActions(
    control: () => FormControl<string | null>,
  ): BaseDropdownActions {
    const uuid = toSignal(
      toObservable(computed(control)).pipe(
        switchMap((control) =>
          control.valueChanges.pipe(startWith(control.value)),
        ),
      ),
      { initialValue: null },
    );
    return {
      link: computed(() => this.getPreviewLink(uuid())),
      hasAdd: computed(() => this.authStore.hasPermission('bases.add')),
      add: () => ActionHelper.openRoute(BasesRoute.ADD_BASE),
    };
  }

  public openModal(base: BaseModalData): void {
    this.modalService.open<BaseModalData>(BaseModalComponent, base);
  }

  public edit(uuid: string, options: HelperOptions): void {
    this.router.navigateByUrl(
      BasesRoute.EDIT_BASE.replace(':uuid', uuid),
      NavigationHelper.withOrigin(this.router),
    );
    CommonHelper.handleOptions(uuid, options);
  }

  public delete(base: Base, options: HelperOptions): void {
    this.modalService
      .open<OnyxConfirmModalData, boolean>(OnyxConfirmModalComponent, {
        heading: this.translateService.instant(
          `${this.I18N}.confirmModal.delete.heading`,
          { base: base.name },
        ),
        message: `${this.I18N}.confirmModal.delete.description`,
        confirmButtonText: 'labels.delete',
        confirmButtonColor: 'red',
        action: () => this.basesService.deleteBase(base.uuid),
      })
      .result.subscribe((ok) => {
        if (!ok) return;

        this.toastService.showSuccess({
          message: `${this.I18N}.toasts.baseDeleted`,
        });
        CommonHelper.handleOptions(base, options);
      });
  }

  public static isBaseType(base: SimplifiedBase | Base): base is Base {
    return 'businessHours' in base;
  }
}
